import { ref, watch } from "vue";
import { defineStore, storeToRefs } from "pinia";
import { watchDebounced } from "@vueuse/core";
import handleError from "@composables/handleError";
import useMyFetch from "@stores/useMyFetch";
import { useSurveyStore } from "@stores/survey/survey";
import type { Interview } from "@/types";

export const useCurrentInterviewStore = defineStore("currentInterview", () => {
  const { token: surveyToken } = storeToRefs(useSurveyStore());

  const currentInterview = ref<Partial<Interview> | null>(null);
  const personalNotes = ref("");

  const setCurrentInterview = (interview: Partial<Interview> | null) => (currentInterview.value = interview);
  const clearCurrentInterview = () => (currentInterview.value = null);

  const updatePersonalNotes = async () => {
    try {
      await useMyFetch(`surveys/${surveyToken.value}/survey_customers/update_personal_notes`)
        .post({
          survey_customer_id: currentInterview.value?.id,
          personal_notes: currentInterview.value?.personal_notes,
        })
        .json();
    } catch (err) {
      handleError(err);
    }
  };

  watch(
    () => currentInterview.value?.id,
    () => {
      personalNotes.value = currentInterview.value?.personal_notes || "";
    },
    { immediate: true },
  );

  watchDebounced(
    personalNotes,
    async (notes) => {
      if (!currentInterview.value?.id) return;

      currentInterview.value.personal_notes = notes;
      await updatePersonalNotes();
    },
    { debounce: 500 },
  );

  return {
    currentInterview,
    setCurrentInterview,
    clearCurrentInterview,
    personalNotes,
  };
});
